// src/components/admin/Category/CategoryPricingSettingsForm.jsx
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../../../ui/button'; // Updated import
import { Input } from '../../../ui/input'; // Updated import
import { Label } from '../../../ui/label'; // Updated import
import { Checkbox } from '../../../ui/checkbox'; // Updated import
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
//import { cn } from '../../../../lib/utils'; // For conditional class merging
import { Loader2 } from 'lucide-react'; // Import loader icon

const ModifierTypeEnum = {
    ABSOLUTE: "ABSOLUTE",
    PERCENTAGE: "PERCENTAGE",
};

// Default values for a new setting record
const defaultSettingValues = {
    base_price_coins: 1,
    is_pricing_active: true,
    is_variable_pricing_active: true,
    min_price_coins: '',
    max_price_coins: '',
    time_reduction_config_enabled: false,
    interaction_pricing_config_enabled: false,
    interaction_hold_minutes_config: 5, // Defaulted to a number as per original
    // Time-delay discount config
    time_discount_delay_hours: '',
    time_discount_modifier_type: '',
    time_discount_value_absolute: '',
    time_discount_value_percentage_display: '', // whole percent (e.g. 15 for 15%); converted to a rate on submit
    time_discount_label: 'Unsold Leads Discount', // display name shown to sellers
};

export default function CategoryPricingSettingsForm({ initialData, onSave, onCancel, isLoading }) {
    const { control, handleSubmit, reset, formState: { errors, isDirty }, getValues, watch } = useForm({
        defaultValues: defaultSettingValues, // Initialize with defaults
    });

    watch(["base_price_coins", "min_price_coins", "max_price_coins"]);
    const timeDiscountEnabled = watch("time_reduction_config_enabled");
    const timeDiscountType = watch("time_discount_modifier_type");

    useEffect(() => {
        if (initialData) {
            const storedRate = initialData.time_discount_value_percentage_rate;
            reset({
                base_price_coins: initialData.base_price_coins ?? 1,
                is_pricing_active: initialData.is_pricing_active !== undefined ? initialData.is_pricing_active : true,
                is_variable_pricing_active: initialData.is_variable_pricing_active !== undefined ? initialData.is_variable_pricing_active : true,
                min_price_coins: initialData.min_price_coins ?? '', // Ensure it's not undefined
                max_price_coins: initialData.max_price_coins ?? '', // Ensure it's not undefined
                time_reduction_config_enabled: initialData.time_reduction_config_enabled || false,
                interaction_pricing_config_enabled: initialData.interaction_pricing_config_enabled || false,
                interaction_hold_minutes_config: initialData.interaction_hold_minutes_config ?? 5, // Ensure it's not undefined
                // Time-delay discount config
                time_discount_delay_hours: initialData.time_discount_delay_hours ?? '',
                time_discount_modifier_type: initialData.time_discount_modifier_type ?? '',
                time_discount_value_absolute: initialData.time_discount_value_absolute ?? '',
                time_discount_value_percentage_display:
                    storedRate !== null && storedRate !== undefined
                        ? parseFloat((parseFloat(storedRate) * 100).toFixed(2))
                        : '',
                time_discount_label: initialData.time_discount_label ?? 'Unsold Leads Discount',
            });
        } else {
            reset(defaultSettingValues);
        }
    }, [initialData, reset]);

    const onSubmit = (data) => {
        const payload = {
            ...data,
            base_price_coins: data.base_price_coins === '' || data.base_price_coins === null ? null : parseInt(data.base_price_coins, 10),
            min_price_coins: data.min_price_coins === '' || data.min_price_coins === null ? null : parseInt(data.min_price_coins, 10),
            max_price_coins: data.max_price_coins === '' || data.max_price_coins === null ? null : parseInt(data.max_price_coins, 10),
            interaction_hold_minutes_config: data.interaction_hold_minutes_config === '' || data.interaction_hold_minutes_config === null ? null : parseInt(data.interaction_hold_minutes_config, 10),
        };

        // RHF valueAsNumber handles NaN conversion; ensure payload matches backend expectations (null vs NaN)
        // The parseInt above will result in NaN if parsing fails. API might expect null.
        if (isNaN(payload.base_price_coins)) payload.base_price_coins = null;
        if (isNaN(payload.min_price_coins)) payload.min_price_coins = null;
        if (isNaN(payload.max_price_coins)) payload.max_price_coins = null;
        if (isNaN(payload.interaction_hold_minutes_config)) payload.interaction_hold_minutes_config = null;

        // --- Time-delay discount config ---
        // The form field time_discount_value_percentage_display is a whole percent; the backend
        // stores a rate (0-1). Only one of absolute/percentage is sent, per modifier type.
        delete payload.time_discount_value_percentage_display;
        if (data.time_reduction_config_enabled) {
            const delay = parseInt(data.time_discount_delay_hours, 10);
            payload.time_discount_delay_hours = isNaN(delay) ? null : delay;
            payload.time_discount_modifier_type = data.time_discount_modifier_type || null;

            if (data.time_discount_modifier_type === ModifierTypeEnum.ABSOLUTE) {
                const absVal = parseInt(data.time_discount_value_absolute, 10);
                payload.time_discount_value_absolute = isNaN(absVal) ? null : absVal;
                payload.time_discount_value_percentage_rate = null;
            } else if (data.time_discount_modifier_type === ModifierTypeEnum.PERCENTAGE) {
                const displayVal = parseFloat(data.time_discount_value_percentage_display);
                payload.time_discount_value_percentage_rate = isNaN(displayVal)
                    ? null
                    : parseFloat((displayVal / 100).toFixed(4));
                payload.time_discount_value_absolute = null;
            } else {
                payload.time_discount_value_absolute = null;
                payload.time_discount_value_percentage_rate = null;
            }
            payload.time_discount_label = (data.time_discount_label || '').trim() || null;
        } else {
            // Disabled: clear all discount config so stale values don't linger.
            payload.time_discount_delay_hours = null;
            payload.time_discount_modifier_type = null;
            payload.time_discount_value_absolute = null;
            payload.time_discount_value_percentage_rate = null;
            payload.time_discount_label = null;
        }

        onSave(payload);
    };
    
    const errorTextClass = "text-sm text-destructive mt-1";

    // Helper to manage value for number inputs
    const getNumberInputValue = (value) => {
        if (value === null || typeof value === 'undefined' || isNaN(value)) {
            return '';
        }
        return String(value);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
                <Label htmlFor="base_price_coins">Base Price (Coins) <span className="text-destructive">*</span></Label>
                <Controller
                    name="base_price_coins"
                    control={control}
                    rules={{
                        required: "Base price is required.",
                        valueAsNumber: true,
                        min: { value: 1, message: "Base price must be at least 1." },
                        validate: value => !isNaN(value) || "Base price must be a valid number."
                    }}
                    render={({ field }) => (
                        <Input
                            type="number"
                            id="base_price_coins"
                            {...field}
                            value={getNumberInputValue(field.value)}
                            onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                            className="mt-1"
                        />
                    )}
                />
                {errors.base_price_coins && <p className={errorTextClass}>{errors.base_price_coins.message}</p>}
            </div>

            <div>
                <Label htmlFor="min_price_coins">Min Price (Coins) <span className="text-destructive">*</span></Label>
                <Controller
                    name="min_price_coins"
                    control={control}
                    rules={{
                        required: "Min price is required.",
                        valueAsNumber: true,
                        min: { value: 0, message: "Min price cannot be negative." },
                        validate: {
                            isNumber: value => !isNaN(value) || "Min price must be a valid number.",
                            lessThanBase: value => {
                                const basePrice = getValues("base_price_coins");
                                if (isNaN(value) || isNaN(basePrice) || basePrice === null) return true;
                                return parseFloat(value) <= parseFloat(basePrice) || "Min price cannot be more than base price.";
                            },
                        }
                    }}
                    render={({ field }) => (
                        <Input
                            type="number"
                            id="min_price_coins"
                            {...field}
                            value={getNumberInputValue(field.value)}
                            onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                            className="mt-1"
                            placeholder="e.g., 10"
                        />
                    )}
                />
                {errors.min_price_coins && <p className={errorTextClass}>{errors.min_price_coins.message}</p>}
            </div>

            <div>
                <Label htmlFor="max_price_coins">Max Price (Coins) <span className="text-destructive">*</span></Label>
                <Controller
                    name="max_price_coins"
                    control={control}
                    rules={{
                        required: "Max price is required.",
                        valueAsNumber: true,
                        min: { value: 0, message: "Max price cannot be negative." },
                        validate: {
                            isNumber: value => !isNaN(value) || "Max price must be a valid number.",
                            greaterThanMin: value => {
                                const minPrice = getValues("min_price_coins");
                                if (isNaN(value) || isNaN(minPrice) || minPrice === null) return true;
                                return parseFloat(value) >= parseFloat(minPrice) || "Max price cannot be less than min price.";
                            },
                            greaterThanBase: value => {
                                const basePrice = getValues("base_price_coins");
                                if (isNaN(value) || isNaN(basePrice) || basePrice === null) return true;
                                return parseFloat(value) >= parseFloat(basePrice) || "Max price cannot be less than base price.";
                            }
                        }
                    }}
                    render={({ field }) => (
                        <Input
                            type="number"
                            id="max_price_coins"
                            {...field}
                            value={getNumberInputValue(field.value)}
                            onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                            className="mt-1"
                            placeholder="e.g., 1000"
                        />
                    )}
                />
                {errors.max_price_coins && <p className={errorTextClass}>{errors.max_price_coins.message}</p>}
            </div>
            
            <div className="flex items-center space-x-2">
                <Controller
                    name="is_pricing_active"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            id="is_pricing_active"
                            checked={!!field.value}
                            onCheckedChange={field.onChange} // RHF handles boolean
                        />
                    )}
                />
                <Label htmlFor="is_pricing_active" className="font-normal">Enable Pricing for this Category</Label>
            </div>

            <div className="flex items-center space-x-2">
                <Controller
                    name="is_variable_pricing_active"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            id="is_variable_pricing_active"
                            checked={!!field.value}
                            onCheckedChange={field.onChange}
                        />
                    )}
                />
                <Label htmlFor="is_variable_pricing_active" className="font-normal">Enable Rule-Based Variable Pricing</Label>
            </div>
            
            <div className="border rounded-md p-4 space-y-4">
                <div className="flex items-center space-x-2">
                    <Controller
                        name="time_reduction_config_enabled"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id="time_reduction_config_enabled"
                                checked={!!field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                    <Label htmlFor="time_reduction_config_enabled" className="font-normal">Enable Time-Delay Discount</Label>
                </div>

                {timeDiscountEnabled && (
                    <div className="space-y-4 pl-6">
                        <p className="text-xs text-muted-foreground">
                            Leads still unpurchased-at-full-price get discounted once this many hours have passed since the lead was placed.
                            If a professional buys at full price before the discount kicks in, that lead stays at full price permanently.
                        </p>

                        <div>
                            <Label htmlFor="time_discount_label">Discount Label <span className="text-destructive">*</span></Label>
                            <Controller
                                name="time_discount_label"
                                control={control}
                                rules={{ validate: value => (!timeDiscountEnabled || (value && value.trim() !== '')) || "Discount label is required." }}
                                render={({ field }) => (
                                    <Input
                                        type="text"
                                        id="time_discount_label"
                                        {...field}
                                        value={field.value ?? ''}
                                        className="mt-1"
                                        placeholder="e.g., Unsold Leads Discount"
                                    />
                                )}
                            />
                            <p className="text-xs text-muted-foreground mt-1">Shown to sellers as the discount name.</p>
                            {errors.time_discount_label && <p className={errorTextClass}>{errors.time_discount_label.message}</p>}
                        </div>

                        <div>
                            <Label htmlFor="time_discount_delay_hours">Delay (Hours) <span className="text-destructive">*</span></Label>
                            <Controller
                                name="time_discount_delay_hours"
                                control={control}
                                rules={{
                                    validate: value => {
                                        if (!timeDiscountEnabled) return true;
                                        if (value === '' || value === null) return "Delay is required.";
                                        return parseInt(value, 10) >= 0 || "Delay must be zero or positive.";
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        type="number"
                                        id="time_discount_delay_hours"
                                        {...field}
                                        value={getNumberInputValue(field.value)}
                                        onChange={e => field.onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                        className="mt-1"
                                        placeholder="e.g., 48"
                                    />
                                )}
                            />
                            {errors.time_discount_delay_hours && <p className={errorTextClass}>{errors.time_discount_delay_hours.message}</p>}
                        </div>

                        <div>
                            <Label htmlFor="time_discount_modifier_type">Discount Type <span className="text-destructive">*</span></Label>
                            <Controller
                                name="time_discount_modifier_type"
                                control={control}
                                rules={{ validate: value => (!timeDiscountEnabled || !!value) || "Discount type is required." }}
                                render={({ field }) => (
                                    <Select value={field.value || ''} onValueChange={field.onChange}>
                                        <SelectTrigger id="time_discount_modifier_type" className="mt-1">
                                            <SelectValue placeholder="Select discount type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ModifierTypeEnum.ABSOLUTE}>Absolute (Coins off)</SelectItem>
                                            <SelectItem value={ModifierTypeEnum.PERCENTAGE}>Percentage (% off)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {errors.time_discount_modifier_type && <p className={errorTextClass}>{errors.time_discount_modifier_type.message}</p>}
                        </div>

                        {timeDiscountType === ModifierTypeEnum.ABSOLUTE && (
                            <div>
                                <Label htmlFor="time_discount_value_absolute">Discount (Coins off) <span className="text-destructive">*</span></Label>
                                <Controller
                                    name="time_discount_value_absolute"
                                    control={control}
                                    rules={{
                                        validate: value => {
                                            if (!timeDiscountEnabled || timeDiscountType !== ModifierTypeEnum.ABSOLUTE) return true;
                                            if (value === '' || value === null) return "Discount amount is required.";
                                            return parseInt(value, 10) > 0 || "Discount must be a positive number of coins.";
                                        },
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            id="time_discount_value_absolute"
                                            {...field}
                                            value={getNumberInputValue(field.value)}
                                            onChange={e => field.onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                            className="mt-1"
                                            placeholder="e.g., 20"
                                        />
                                    )}
                                />
                                {errors.time_discount_value_absolute && <p className={errorTextClass}>{errors.time_discount_value_absolute.message}</p>}
                            </div>
                        )}

                        {timeDiscountType === ModifierTypeEnum.PERCENTAGE && (
                            <div>
                                <Label htmlFor="time_discount_value_percentage_display">Discount (%) <span className="text-destructive">*</span></Label>
                                <Controller
                                    name="time_discount_value_percentage_display"
                                    control={control}
                                    rules={{
                                        validate: value => {
                                            if (!timeDiscountEnabled || timeDiscountType !== ModifierTypeEnum.PERCENTAGE) return true;
                                            if (value === '' || value === null) return "Discount percentage is required.";
                                            const num = parseFloat(value);
                                            if (isNaN(num)) return "Must be a valid number.";
                                            return (num > 0 && num < 100) || "Percentage must be between 0 and 100 (exclusive).";
                                        },
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            step="0.01"
                                            id="time_discount_value_percentage_display"
                                            {...field}
                                            value={getNumberInputValue(field.value)}
                                            onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                                            className="mt-1"
                                            placeholder="e.g., 15 for 15% off"
                                        />
                                    )}
                                />
                                {errors.time_discount_value_percentage_display && <p className={errorTextClass}>{errors.time_discount_value_percentage_display.message}</p>}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <p className="text-sm text-muted-foreground pt-2">Configuration for future features (can be set now):</p>
             <div className="flex items-center space-x-2">
                <Controller
                    name="interaction_pricing_config_enabled"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            id="interaction_pricing_config_enabled"
                            checked={!!field.value}
                            onCheckedChange={field.onChange}
                        />
                    )}
                />
                <Label htmlFor="interaction_pricing_config_enabled" className="font-normal">Enable Interaction-Based Pricing (Config)</Label>
            </div>
             <div>
                <Label htmlFor="interaction_hold_minutes_config">Interaction Hold Minutes (Config, Optional)</Label>
                <Controller
                    name="interaction_hold_minutes_config"
                    control={control}
                    rules={{
                        min: { value: 0, message: "Cannot be negative" },
                        validate: value => value === null || value === '' || !isNaN(parseFloat(value)) || "Must be a valid number if provided.",
                        valueAsNumber: true,
                    }}
                    render={({ field }) => (
                        <Input
                            type="number"
                            id="interaction_hold_minutes_config"
                            {...field}
                            value={getNumberInputValue(field.value)}
                            onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                            className="mt-1"
                            placeholder="e.g., 5"
                        />
                    )}
                />
                {errors.interaction_hold_minutes_config && <p className={errorTextClass}>{errors.interaction_hold_minutes_config.message}</p>}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                    Cancel
                </Button>
                <Button type="submit" variant="default" disabled={isLoading || !isDirty}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {initialData ? 'Save Changes' : 'Save Settings'}
                </Button>
            </div>
        </form>
    );
}